import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  HStack,
  Icon,
  Text
} from '@chakra-ui/react'
import HelpWizard from 'components/UX/QuickViewer/components/HelpWizard'
import HelpVideoGuides from 'components/UX/QuickViewer/components/HelpVideoGuides'
import { usePreviewerContext } from 'context/PreviewerProvider'
import React from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { VscChevronDown, VscChevronRight } from 'react-icons/vsc'
import { PreviewComponentType } from 'context/PreviewerProvider/types'
import { useLocation } from 'react-router-dom'

const DefaultView: React.FC = () => {
  const { onBoardingCompleted, previewComponent, setPreviewComponent } = usePreviewerContext()
  const [index, setIndex] = React.useState<number>(onBoardingCompleted ? 0 : 1)

  const location = useLocation()
  const pathname = location?.pathname

  // Clear the injected preview when the user NAVIGATES away — not on mount.
  // An effect keyed on pathname alone also fires when the drawer first
  // opens, which wiped a preview that was set while the drawer was closed
  // (e.g. PIA re-opening after analytics full screen).
  const prevPathRef = React.useRef(pathname)
  React.useEffect(() => {
    if (prevPathRef.current === pathname) return
    prevPathRef.current = pathname
    if (previewComponent) {
      setPreviewComponent?.(null)
    }
  }, [pathname, previewComponent, setPreviewComponent])
  React.useEffect(() => {
    if (onBoardingCompleted) {
      setIndex(0)
    }
  }, [onBoardingCompleted])

  // An injected preview (PIA) always renders first — expand it when it
  // arrives, so opening the drawer shows the assistant, not a folded row.
  React.useEffect(() => {
    if (previewComponent) {
      setIndex(0)
    }
  }, [previewComponent])

  const options = [
    ...(previewComponent ? [previewComponent] : []),
    {
      title: 'Quick Help',
      icon: null,
      content: <HelpVideoGuides />,
      color: 'gray.500'
    },
    {
      title: 'Onboarding Steps',
      icon: onBoardingCompleted ? FaCheckCircle : null,
      content: <HelpWizard />,
      color: onBoardingCompleted ? 'gray.400' : 'gray.500'
    }
  ] as unknown as PreviewComponentType[]

  return (
    <Accordion allowToggle width="100%" index={index} p={0} borderColor="gray.100">
      {options.map((option, index) => (
        <AccordionItem
          p={0}
          key={index}
          border="none"
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  px={4}
                  py={3}
                  borderRadius={0}
                  bg="transparent"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => {
                    setIndex(index)
                  }}
                >
                  <HStack w="100%" spacing={2}>
                    {option?.icon && <Icon as={option?.icon} boxSize={4} color={option.color} />}
                    <Box flex="1" textAlign="left">
                      <Text
                        fontSize="xs"
                        fontWeight={isExpanded ? '600' : '400'}
                        color="gray.700"
                        letterSpacing="-0.01em"
                      >
                        {option?.title}
                      </Text>
                    </Box>
                  </HStack>
                  <Icon
                    as={isExpanded ? VscChevronDown : VscChevronRight}
                    boxSize={4}
                    color="gray.400"
                  />
                </AccordionButton>
              </h2>
              <AccordionPanel pt={2} pb={5} px={4}>
                {option?.content}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      ))}
    </Accordion>
  )
}

export default DefaultView
